import { useMemo } from 'react';
import { addDays, format, isSameDay, startOfWeek } from 'date-fns';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Calendar } from '@/components/calendar/Calendar';
import { ChronoList } from '@/components/patterns/ChronoList';
import type {
  CalendarEvent,
  CalendarLayer,
  CalendarLayerInterval,
  EventStatus,
} from '@/lib/calendar/types';
import type {
  StaffAvailableSlot,
  StaffBooking,
  CalendarBlock,
} from '@/services/api/staff.service';
import type {
  WeekScheduleDay,
  ProviderAvailability,
  AvailabilityOverride,
} from '@/services/api/availability.service';
import { getDateLocale } from '@/utils/dateLocale';
import { cn } from '@/lib/utils';

export type ScheduleViewMode = 'day' | 'week' | 'month' | 'list';

const BACKEND_TO_EVENT_STATUS: Record<string, EventStatus> = {
  confirmed: 'confirmed',
  pending: 'pending',
  in_progress: 'in-progress',
  completed: 'done',
  done: 'done',
  cancelled: 'cancelled',
  no_show: 'cancelled',
};

function mapBookingStatus(status: string | undefined): EventStatus {
  if (!status) return 'confirmed';
  return BACKEND_TO_EVENT_STATUS[status] ?? 'confirmed';
}

function parseTimeOnDate(date: Date, time: string): Date {
  const [h, m] = time.split(':').map(Number);
  const out = new Date(date);
  out.setHours(h, m ?? 0, 0, 0);
  return out;
}

function isGroupClassSlot(slot: StaffAvailableSlot): boolean {
  const cap = slot.max_capacity ?? slot.service?.max_capacity ?? 1;
  return (cap ?? 1) > 1;
}

interface StaffMobileCalendarProps {
  viewMode: ScheduleViewMode;
  currentDate: Date;
  slots: StaffAvailableSlot[];
  bookings: StaffBooking[];
  weekSchedule: WeekScheduleDay[];
  availabilityOverrides: AvailabilityOverride[];
  calendarBlocks: CalendarBlock[];
  onCreateSlot: () => void;
  onSlotClick: (slot: StaffAvailableSlot) => void;
  onBookingClick?: (booking: StaffBooking) => void;
  onAvailabilityClick?: (avail: ProviderAvailability, date?: string) => void;
  onOverrideClick?: (override: AvailabilityOverride) => void;
  onEditGroupClass?: (slot: StaffAvailableSlot) => void;
  onDateChange?: (d: Date) => void;
  /** Lets a month-cell tap drill into that day's view. */
  onViewModeChange?: (view: ScheduleViewMode) => void;
  workingHoursStart?: number;
  workingHoursEnd?: number;
  className?: string;
}

/**
 * StaffMobileCalendar — mobile-friendly schedule view that wraps the unified
 * `<Calendar>` for day/week/month and falls back to a ChronoList for the
 * dedicated "list" view. Built specifically for `pages/staff/schedule.tsx`
 * so the page can drop its dependency on the legacy ResponsiveViewSwitcher
 * / MobileDayView / MobileWeekView / MobileMonthView / MobileListView stack.
 */
export function StaffMobileCalendar({
  viewMode,
  currentDate,
  slots,
  bookings,
  weekSchedule,
  availabilityOverrides,
  calendarBlocks,
  onCreateSlot,
  onSlotClick,
  onBookingClick,
  onAvailabilityClick,
  onOverrideClick,
  onEditGroupClass,
  onDateChange,
  onViewModeChange,
  workingHoursStart = 7,
  workingHoursEnd = 22,
  className,
}: StaffMobileCalendarProps) {
  const { t } = useTranslation('staff');

  const visibleDates = useMemo(() => {
    if (viewMode === 'week') {
      const ws = startOfWeek(currentDate, { weekStartsOn: 1 });
      return Array.from({ length: 7 }, (_, i) => addDays(ws, i));
    }
    return [currentDate];
  }, [viewMode, currentDate]);

  const events: CalendarEvent[] = useMemo(() => {
    if (viewMode === 'list') return [];
    if (viewMode === 'month') {
      return bookings
        .map((b) => {
          const start = new Date(b.start_time);
          const end = new Date(b.end_time);
          return {
            id: `b-${b.id}`,
            start,
            end,
            title: b.customer_full_name,
            subtitle: b.service_name,
            status: mapBookingStatus(b.status),
            columnId: format(start, 'yyyy-MM-dd'),
            meta: { kind: 'booking', booking: b },
          } satisfies CalendarEvent;
        })
        .filter((e) => !Number.isNaN(e.start.getTime()));
    }
    const evs: CalendarEvent[] = [];
    for (const b of bookings) {
      const start = new Date(b.start_time);
      if (Number.isNaN(start.getTime())) continue;
      if (!visibleDates.some((d) => isSameDay(d, start))) continue;
      evs.push({
        id: `b-${b.id}`,
        start,
        end: new Date(b.end_time),
        title: b.customer_full_name,
        subtitle: b.service_name,
        status: mapBookingStatus(b.status),
        columnId: format(start, 'yyyy-MM-dd'),
        meta: { kind: 'booking', booking: b },
      });
    }
    return evs;
  }, [viewMode, bookings, visibleDates]);

  const layers: CalendarLayer[] = useMemo(() => {
    if (viewMode === 'month' || viewMode === 'list') return [];

    const availability: CalendarLayerInterval[] = [];
    const overrideAvailable: CalendarLayerInterval[] = [];
    const overrideBlocked: CalendarLayerInterval[] = [];
    const busy: CalendarLayerInterval[] = [];
    const groupClass: CalendarLayerInterval[] = [];

    const scheduleByDow = new Map<number, ProviderAvailability[]>();
    weekSchedule.forEach((day) => scheduleByDow.set(day.day_of_week, day.time_ranges));

    const overridesByDate = new Map<string, AvailabilityOverride[]>();
    availabilityOverrides.forEach((o) => {
      const arr = overridesByDate.get(o.date) ?? [];
      arr.push(o);
      overridesByDate.set(o.date, arr);
    });

    visibleDates.forEach((date) => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const dow = date.getDay();
      const ranges = scheduleByDow.get(dow) ?? [];
      const dayOverrides = overridesByDate.get(dateStr) ?? [];
      const blockedByOverride = dayOverrides.some((o) => o.override_type === 'unavailable');

      // Week view on mobile is space-constrained — service-name labels make the
      // green blocks visually noisy. Hide them in week view and keep them for day.
      const showLayerLabels = viewMode === 'day';

      if (!blockedByOverride) {
        ranges.forEach((r) => {
          const svcNames = r.services?.map((s) => s.name).filter(Boolean) ?? [];
          const label = !showLayerLabels || svcNames.length === 0 ? undefined : svcNames.join(', ');
          availability.push({
            id: `avail-${dateStr}-${r.id}`,
            start: parseTimeOnDate(date, r.start_time),
            end: parseTimeOnDate(date, r.end_time),
            columnId: dateStr,
            label,
            data: r,
          });
        });
      }

      dayOverrides.forEach((o) => {
        if (!o.start_time || !o.end_time) return;
        const svcNames = o.services?.map((s) => s.name).filter(Boolean) ?? [];
        const svcLabel = svcNames.length > 0 ? svcNames.join(', ') : undefined;
        const interval: CalendarLayerInterval = {
          id: `override-${o.id}-${dateStr}`,
          start: parseTimeOnDate(date, o.start_time),
          end: parseTimeOnDate(date, o.end_time),
          columnId: dateStr,
          label: showLayerLabels ? (svcLabel ?? o.reason ?? undefined) : undefined,
          data: o,
        };
        if (o.override_type === 'extra') {
          overrideAvailable.push(interval);
        } else {
          overrideBlocked.push(interval);
        }
      });
    });

    calendarBlocks.forEach((cb) => {
      const start = new Date(cb.start_time);
      if (Number.isNaN(start.getTime())) return;
      const dateStr = format(start, 'yyyy-MM-dd');
      if (!visibleDates.some((d) => format(d, 'yyyy-MM-dd') === dateStr)) return;
      busy.push({
        id: `busy-${cb.id}`,
        start,
        end: new Date(cb.end_time),
        columnId: dateStr,
        label: cb.reason || undefined,
        data: cb,
      });
    });

    slots.forEach((s) => {
      if (!s.start_time || !isGroupClassSlot(s)) return;
      const start = new Date(s.start_time);
      if (Number.isNaN(start.getTime())) return;
      const dateStr = format(start, 'yyyy-MM-dd');
      if (!visibleDates.some((d) => format(d, 'yyyy-MM-dd') === dateStr)) return;
      const cap = s.max_capacity ?? s.service?.max_capacity ?? 0;
      const used = s.current_bookings ?? 0;
      const svcName = s.service?.name ?? 'Class';
      groupClass.push({
        id: `gc-${s.id}`,
        start,
        end: new Date(s.end_time),
        columnId: dateStr,
        label: `${svcName} · ${used}/${cap}`,
        data: s,
      });
    });

    const out: CalendarLayer[] = [];
    if (availability.length) {
      out.push({
        id: 'availability',
        variant: 'available',
        intervals: availability,
        z: 0,
        onIntervalClick: onAvailabilityClick
          ? (i) => onAvailabilityClick(i.data as ProviderAvailability, format(i.start, 'yyyy-MM-dd'))
          : undefined,
      });
    }
    if (busy.length) {
      out.push({ id: 'busy', variant: 'busy', intervals: busy, z: 1 });
    }
    if (overrideAvailable.length) {
      out.push({
        id: 'override-available',
        variant: 'override-available',
        intervals: overrideAvailable,
        z: 2,
        onIntervalClick: onOverrideClick
          ? (i) => onOverrideClick(i.data as AvailabilityOverride)
          : undefined,
      });
    }
    if (overrideBlocked.length) {
      out.push({
        id: 'override-blocked',
        variant: 'override-blocked',
        intervals: overrideBlocked,
        z: 2,
        onIntervalClick: onOverrideClick
          ? (i) => onOverrideClick(i.data as AvailabilityOverride)
          : undefined,
      });
    }
    if (groupClass.length) {
      out.push({
        id: 'group-class',
        variant: 'group-class',
        intervals: groupClass,
        z: 3,
        onIntervalClick: onEditGroupClass
          ? (i) => onEditGroupClass(i.data as StaffAvailableSlot)
          : undefined,
      });
    }
    return out;
  }, [
    viewMode,
    visibleDates,
    weekSchedule,
    availabilityOverrides,
    calendarBlocks,
    slots,
    onAvailabilityClick,
    onOverrideClick,
    onEditGroupClass,
  ]);

  if (viewMode === 'list') {
    return (
      <StaffMobileList
        slots={slots}
        onCreateSlot={onCreateSlot}
        onEditSlot={onSlotClick}
        onEditGroupClass={onEditGroupClass}
        className={className}
      />
    );
  }

  return (
    <Calendar
      view={viewMode}
      date={currentDate}
      events={events}
      layers={layers}
      hideViewTabs
      bodyFill
      workingHours={{ start: workingHoursStart, end: workingHoursEnd }}
      onDateChange={onDateChange}
      onEventClick={(ev) => {
        const meta = ev.meta as { kind?: string; booking?: StaffBooking } | undefined;
        if (meta?.kind === 'booking' && meta.booking && onBookingClick) {
          onBookingClick(meta.booking);
        }
      }}
      onSlotClick={(slot) => {
        // A month cell is a whole day, not a time slot — drill into it.
        if (viewMode === 'month') {
          onDateChange?.(slot.start);
          onViewModeChange?.('day');
          return;
        }
        onCreateSlot();
      }}
      className={cn(
        // Month grows to its full grid (the page scrolls); day/week fill the
        // viewport-sized card and scroll internally.
        viewMode === 'month' ? 'h-auto shrink-0' : 'flex-1 min-h-0',
        className,
      )}
    />
  );
}

interface StaffMobileListProps {
  slots: StaffAvailableSlot[];
  onCreateSlot: () => void;
  onEditSlot: (slot: StaffAvailableSlot) => void;
  onEditGroupClass?: (slot: StaffAvailableSlot) => void;
  className?: string;
}

function StaffMobileList({
  slots,
  onCreateSlot,
  onEditSlot,
  onEditGroupClass,
  className,
}: StaffMobileListProps) {
  const { t } = useTranslation('staff');
  const locale = getDateLocale();

  const sortedSlots = useMemo(
    () =>
      [...slots]
        .filter((s) => !!s.start_time)
        .sort((a, b) => new Date(a.start_time!).getTime() - new Date(b.start_time!).getTime()),
    [slots],
  );

  const handleEdit = (slot: StaffAvailableSlot) => {
    if (isGroupClassSlot(slot) && onEditGroupClass) {
      onEditGroupClass(slot);
      return;
    }
    onEditSlot(slot);
  };

  return (
    <div className={cn('relative flex h-full flex-col bg-surface', className)}>
      <ChronoList
        items={sortedSlots}
        dateAccessor={(s) => new Date(s.start_time!)}
        itemKey={(s) => s.id}
        locale={locale}
        emptyState={
          <div className="flex flex-col items-center justify-center px-4 py-16 text-center text-soft">
            <p className="text-body font-medium text-ink">
              {t('schedule.listView.noSlots', 'No time slots scheduled')}
            </p>
            <p className="text-body-sm">
              {t('schedule.listView.createHint', 'Create your first slot to get started')}
            </p>
          </div>
        }
        renderItem={(slot) => {
          const start = new Date(slot.start_time!);
          const end = new Date(slot.end_time);
          const isGroup = isGroupClassSlot(slot);
          const cap = slot.max_capacity ?? slot.service?.max_capacity ?? 0;
          const used = slot.current_bookings ?? 0;
          const booked = slot.booked && !isGroup;
          return (
            <button
              type="button"
              onClick={() => handleEdit(slot)}
              className={cn(
                'flex w-full items-center justify-between gap-3 border-b border-line px-4 py-3 text-left transition-colors',
                'hover:bg-surface-2/60 active:bg-surface-2',
              )}
            >
              <div className="min-w-0">
                <div className="text-body-sm font-semibold tabular-nums text-ink">
                  {format(start, 'HH:mm')} – {format(end, 'HH:mm')}
                </div>
                <div className="truncate text-body-sm text-soft">
                  {slot.service?.name ?? t('schedule.listView.unnamedService', 'Service')}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {isGroup && (
                  <span className="rounded-pill bg-brand-50 px-2 py-0.5 text-body-xs font-medium text-brand-700">
                    {used}/{cap}
                  </span>
                )}
                {booked && (
                  <span className="rounded-pill bg-status-booked/15 px-2 py-0.5 text-body-xs font-medium text-status-booked">
                    {t('schedule.listView.booked', 'Booked')}
                  </span>
                )}
              </div>
            </button>
          );
        }}
      />

      <button
        type="button"
        onClick={onCreateSlot}
        className={cn(
          'fixed bottom-6 right-4 z-20 flex h-14 items-center gap-2 rounded-pill bg-brand-500 px-5 shadow-card-lg',
          'text-body font-semibold text-white transition-transform active:scale-95',
        )}
        style={{ bottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
        aria-label={t('availability.addAvailability', 'Add Availability')}
      >
        <Plus className="h-5 w-5" aria-hidden />
        <span>{t('availability.addAvailability', 'Add Availability')}</span>
      </button>
    </div>
  );
}
